import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ApiClient } from "@/lib/api-client";
import { API_CONFIG } from "@/config/api";
import { toast } from "sonner";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "./ui/input-otp";
import md5 from "md5";
import { PasswordInput } from "./ui/password-input";
interface RegisterFormProps {
  onRegister: (data: RegisterData) => Promise<void>;
  isLoading?: boolean;
  errors?: Record<string, string[]>;
}

export interface RegisterData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  password_confirmation: string;
  company_gst?: string;
  company_name?: string;
}

export function RegisterForm({
  onRegister,
  isLoading,
  errors,
}: RegisterFormProps) {
  const [verifyingGst, setVerifyingGst] = useState(false);
  const [gstVerified, setGstVerified] = useState(false);
  const [gstVerifyError, setGstVerifyError] = useState<string | null>(null);
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);
  const [mobileVerified, setMobileVerified] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [emailOtp, setEmailOtp] = useState("");
  const [mobileOtp, setMobileOtp] = useState("");
  const [mobileOTPRes, setMobileOTPRes] = useState<any>(null);
  const [emailOTPRes, setEmailOTPRes] = useState<any>(null);
  const [otpSentTo, setOtpSentTo] = useState<"email" | "mobile" | null>(null);
  const [step, setStep] = useState<"contact" | "email-otp" | "mobile-otp">(
    "contact"
  );
  const [isLoading1, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailOtpSent, setEmailOtpSent] = useState(false); // new
  const [mobileOtpSent, setMobileOtpSent] = useState(false); // new
  const [formData, setFormData] = useState<RegisterData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    password_confirmation: "",
    company_gst: "",
    company_name: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Remove empty optional fields
    const dataToSubmit = { ...formData };
    if (!dataToSubmit.company_gst) delete dataToSubmit.company_gst;
    if (!dataToSubmit.company_name) delete dataToSubmit.company_name;
    if(!emailVerified){
      toast.error("Please verify your email before registering.");
      return;
    }
    if(!mobileVerified){
      toast.error("Please verify your mobile number before registering.");
      return;
    }
    await onRegister(dataToSubmit);
  };

  const handleChange = (field: keyof RegisterData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getFieldError = (field: string): string | undefined => {
    return errors?.[field]?.[0];
  };

  const verifyGst = async () => {
    console.log(
      "Verifying GST:",
      API_CONFIG,
      `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}`
    );
    const gst = formData.company_gst?.trim();
    const validGst =
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    if (!gst) {
      setGstVerifyError("Enter GST number to verify");
      return;
    } else if (!validGst.test(gst)) {
      setGstVerifyError("Invalid GST number format");
      return;
    }

    const params = {
      tax_number: gst,
      email: formData.email,
      phone: formData.phone,
    };
    setGstVerifyError(null);
    setVerifyingGst(true);
    setGstVerified(false);

    try {
      const url = `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}/api/customer/taxcode-verify`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ params }), // keep same shape as previous ApiClient call
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(
          text || `Request failed with status ${response.status}`
        );
      }

      const respJson = await response.json();

      // Support multiple response shapes
      console.log("GST Verification Response:", respJson);
      const respError = respJson?.response?.error;
      if (respError === true || respError === "invalid_grant") {
        const msg =
          respJson?.response?.message ??
          respJson?.response?.data ??
          "GST verification failed";
        setGeneralError(msg);
        return;
      }
      const payload = respJson?.data ?? respJson;
      const valid = Boolean(payload?.valid ?? respJson?.valid);
      const name = payload?.company_name ?? respJson?.company_name ?? "";

      if (valid) {
        if (name) setFormData((prev) => ({ ...prev, company_name: name }));
        setGstVerified(true);
      } else {
        setGstVerifyError("GST number is invalid");
      }
    } catch (err: any) {
      setGstVerifyError(
        err?.message || err?.response?.data?.message || "Failed to verify GST"
      );
    } finally {
      setVerifyingGst(false);
    }
  };

  const handleSendEmailOtp = async () => {
    if (!emailAddress.trim()) {
      setError("Please enter email address");
      return;
    }

    setError(null);
    // setIsLoading(true);
    try {
      // Call your API to send email OTP
      const response = await fetch(
        `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}/api/customisation/input-validation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: emailAddress, input_type: "email" }),
        }
      );

      if (!response.ok) throw new Error("Failed to send email OTP");
      const respJson = await response.json();
      setEmailOTPRes(respJson);
      setStep("email-otp");
      setOtpSentTo("email");
      if (respJson?.status == true) {
        toast.warning("Email already registered. Please login.");
      } else {
        setEmailOtpSent(true);
        toast.success(`OTP sent to ${emailAddress}`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send email OTP");
      toast.error("Failed to send email OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMobileOtp = async () => {
    if (!phoneNumber.trim() || phoneNumber.length !== 10) {
      setError("Please enter valid 10 digit phone number");
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
      // Call your API to send mobile OTP
      const response: any = await fetch(
        `${API_CONFIG.DEFAULT_IMAGE_BASE_URL}/api/customisation/input-validation`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phone: phoneNumber, input_type: "phone" }),
        }
      );

      if (!response.ok) throw new Error("Failed to send mobile OTP");
      const respJson = await response.json();
      setMobileOTPRes(respJson);
      setStep("mobile-otp");
      setOtpSentTo("mobile");
      console.log("Response from mobile OTP API:", respJson);
      if (respJson?.status == true) {
        toast.warning("Mobile number already registered. Please login.");
      } else {
        setMobileOtpSent(true);
        toast.success(`OTP sent to +91${phoneNumber}`);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to send mobile OTP"
      );
      toast.error("Failed to send mobile OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    if (emailOtp.length !== 6) {
      setError("Please enter 6 digit OTP");
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
        const encriptOtp = md5 (emailOtp);
      console.log("mobileOTPRes",emailOTPRes,encriptOtp);
       if (emailOTPRes && encriptOtp !== emailOTPRes.otp) {
          throw new Error("Invalid OTP");
       }else{
        toast.success("Email verified successfully");
        setEmailVerified(true);
       }
       return;
      const response = await fetch("/api/auth/verify-email-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: emailAddress, otp: emailOtp }),
      });

      if (!response.ok) throw new Error("Invalid OTP");

      setEmailVerified(true);
      toast.success("Email verified successfully");
      // Move to mobile OTP if not verified yet
      if (!mobileVerified) {
        setStep("contact");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid OTP");
      toast.error("Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyMobileOtp = async () => {
    if (mobileOtp.length !== 6) {
      setError("Please enter 6 digit OTP");
      return;
    }

    setError(null);
    setIsLoading(true);
    try {
       const encriptOtp = md5 (mobileOtp);
      console.log("mobileOTPRes",mobileOTPRes,encriptOtp);
       if (mobileOTPRes && encriptOtp !== mobileOTPRes.otp) {
          throw new Error("Invalid OTP");
       }else{
        toast.success("Mobile verified successfully");
        setMobileVerified(true);
       }
       return;
      const response = await fetch("/api/auth/verify-mobile-otp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone: phoneNumber, otp: mobileOtp }),
      });

      if (!response.ok) throw new Error("Invalid OTP");

      setMobileVerified(true);
      toast.success("Mobile verified successfully");

      // If both verified, complete login
      if (emailVerified && mobileVerified) {
        // handleLoginComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid OTP");
      toast.error("Invalid OTP");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsLoading(true);
    try {
      if (otpSentTo === "email") {
        await handleSendEmailOtp();
      } else if (otpSentTo === "mobile") {
        await handleSendMobileOtp();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendEmailOtp = async () => {
    setIsLoading(true);
    try {
      await handleSendEmailOtp();
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendMobileOtp = async () => {
    setIsLoading(true);
    try {
      await handleSendMobileOtp();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="first_name">First Name *</Label>
          <Input
            id="first_name"
            type="text"
            placeholder="Please enter your first name"
            value={formData.first_name}
            onChange={(e) => handleChange("first_name", e.target.value)}
            required
            disabled={isLoading}
            className={getFieldError("first_name") ? "border-red-500" : ""}
          />
          {getFieldError("first_name") && (
            <p className="text-sm text-red-500">
              {getFieldError("first_name")}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="last_name">Last Name *</Label>
          <Input
            id="last_name"
            type="text"
            placeholder="Please enter your last name"
            value={formData.last_name}
            onChange={(e) => handleChange("last_name", e.target.value)}
            required
            disabled={isLoading}
            className={getFieldError("last_name") ? "border-red-500" : ""}
          />
          {getFieldError("last_name") && (
            <p className="text-sm text-red-500">{getFieldError("last_name")}</p>
          )}
        </div>
      </div>

      {/* <div className="space-y-2">
        <Label htmlFor="email">Email *</Label>
        <Input
          id="email"
          type="email"
          placeholder="Please enter your email address"
          value={formData.email}
          onChange={(e) => handleChange("email", e.target.value)}
          required
          disabled={isLoading}
          className={getFieldError("email") ? "border-red-500" : ""}
        />
        {getFieldError("email") && (
          <p className="text-sm text-red-500">{getFieldError("email")}</p>
        )}
      </div> */}
      <div className="space-y-3 p-4 border rounded-lg">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Email Address</label>
          {emailVerified && (
            <span className="text-xs text-green-600 font-semibold">
              ✓ Verified
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Input
            type="email"
            placeholder="Enter your email"
            value={emailAddress}
            onChange={(e) => {setEmailAddress(e.target.value);handleChange("email", e.target.value)}}
            disabled={emailVerified || isLoading}
            className="flex-1"
          />
          <Button
            onClick={handleSendEmailOtp}
            disabled={!emailAddress || emailVerified || isLoading}
            variant={emailVerified ? "outline" : "default"}
          >
            {emailVerified ? "Done" : "Send OTP"}
          </Button>
        </div>
        {emailOtpSent && !emailVerified && (
          <div className="space-y-3 mt-4 p-3 bg-muted rounded">
            <p className="text-sm text-muted-foreground">
              Enter 6-digit OTP sent to {emailAddress}
            </p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={emailOtp} onChange={setEmailOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              onClick={handleVerifyEmailOtp}
              className="w-full"
              disabled={emailOtp.length !== 6 || isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Verify Email OTP
            </Button>
            <button
              onClick={handleResendEmailOtp}
              disabled={isLoading}
              className="text-xs text-primary text-center w-full hover:underline disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>
        )}
      </div>

      {/* <div className="space-y-2">
        <Label htmlFor="phone">Phone *</Label>
        <Input
          id="phone"
          type="tel"
          placeholder="Please enter your phone number"
          value={formData.phone}
          onChange={(e) => handleChange("phone", e.target.value)}
          required
          disabled={isLoading}
          className={getFieldError("phone") ? "border-red-500" : ""}
        />
        {getFieldError("phone") && (
          <p className="text-sm text-red-500">{getFieldError("phone")}</p>
        )}
      </div> */}
      <div className="space-y-3 p-4 border rounded-lg">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium">Phone Number</label>
          {mobileVerified && (
            <span className="text-xs text-green-600 font-semibold">
              ✓ Verified
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <div className="flex items-center justify-center px-3 py-2 border rounded-md bg-muted">
            <span className="font-medium text-sm">+91</span>
          </div>
          <Input
            placeholder="10 digit phone number"
            value={phoneNumber}
            onChange={(e) =>{
              setPhoneNumber(e.target.value.replace(/\D/g, "").slice(0, 10));
              handleChange("phone", e.target.value.replace(/\D/g, "").slice(0, 10));
            }}
            disabled={mobileVerified || isLoading}
            className="flex-1"
          />
          <Button
            onClick={handleSendMobileOtp}
            disabled={phoneNumber.length !== 10 || mobileVerified || isLoading}
            variant={mobileVerified ? "outline" : "default"}
          >
            {mobileVerified ? "Done" : "Send OTP"}
          </Button>
        </div>
        {mobileOtpSent && !mobileVerified && (
          <div className="space-y-3 mt-4 p-3 bg-muted rounded">
            <p className="text-sm text-muted-foreground">
              Enter 6-digit OTP sent to +91{phoneNumber}
            </p>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={mobileOtp} onChange={setMobileOtp}>
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <Button
              onClick={handleVerifyMobileOtp}
              className="w-full"
              disabled={mobileOtp.length !== 6 || isLoading}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Verify Mobile OTP
            </Button>
            <button
              onClick={handleResendMobileOtp}
              disabled={isLoading}
              className="text-xs text-primary text-center w-full hover:underline disabled:opacity-50"
            >
              Resend OTP
            </button>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password">Password *</Label>
        {/* <Input
          id="password"
          type="password"
          placeholder="Enter your password"
          value={formData.password}
          onChange={(e) => handleChange("password", e.target.value)}
          required
          disabled={isLoading}
          className={getFieldError("password") ? "border-red-500" : ""}
        /> */}
        <PasswordInput
      id="password"
      placeholder="Enter your password"
      value={formData.password}
      onChange={(e) => handleChange("password", e.target.value)}
      required
      disabled={isLoading}
      className={getFieldError("password") ? "border-red-500" : ""}
    />
        {getFieldError("password") && (
          <p className="text-sm text-red-500">{getFieldError("password")}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="password_confirmation">Confirm Password *</Label>
        {/* <Input
          id="password_confirmation"
          type="password"
          placeholder="Enter your password again"
          value={formData.password_confirmation}
          onChange={(e) =>
            handleChange("password_confirmation", e.target.value)
          }
          required
          disabled={isLoading}
          className={
            getFieldError("password_confirmation") ? "border-red-500" : ""
          }
        /> */}
        <PasswordInput
      id="password_confirmation"
      placeholder="Confirm password"
      value={formData.password_confirmation}
      onChange={(e) => handleChange("password_confirmation", e.target.value)}
      required
      disabled={isLoading}
      className={getFieldError("password_confirmation") ? "border-red-500" : ""}
    />
        {getFieldError("password_confirmation") && (
          <p className="text-sm text-red-500">
            {getFieldError("password_confirmation")}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="company_gst">Company GST</Label>
        <div className="flex">
          <Input
            id="company_gst"
            type="text"
            placeholder="Optional"
            value={formData.company_gst}
            onChange={(e) => handleChange("company_gst", e.target.value)}
            disabled={isLoading || verifyingGst || gstVerified}
            className={
              getFieldError("company_gst") ? "border-red-500 flex-1" : "flex-1"
            }
          />
          <Button
            type="button"
            onClick={verifyGst}
            disabled={isLoading || verifyingGst || !formData.company_gst}
            variant={gstVerified ? "ghost" : "default"}
            className="whitespace-nowrap"
          >
            {verifyingGst ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : gstVerified ? (
              "Verified"
            ) : (
              "Verify"
            )}
          </Button>
        </div>
        {getFieldError("company_gst") && (
          <p className="text-sm text-red-500">{getFieldError("company_gst")}</p>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="company_name">Company Name</Label>
        <Input
          id="company_name"
          type="text"
          placeholder="Optional"
          value={formData.company_name}
          onChange={(e) => handleChange("company_name", e.target.value)}
          disabled={isLoading}
          className={getFieldError("company_name") ? "border-red-500" : ""}
        />
        {getFieldError("company_name") && (
          <p className="text-sm text-red-500">
            {getFieldError("company_name")}
          </p>
        )}
      </div>

      {errors?.message && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errors.message}</AlertDescription>
        </Alert>
      )}
      {(generalError || gstVerifyError || errors?.message) && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {generalError ?? gstVerifyError ?? errors?.message}
          </AlertDescription>
        </Alert>
      )}

      <Button type="submit" className="w-full" disabled={isLoading}>
        {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        Create Account
      </Button>
    </form>
  );
}


